import { FormConfig, MultipleChoicePanel as MultipleChoicePanelType, GenericMultipleChoicePanel } from "@/app/config/form-config";
import { useState, useEffect } from "react";
import { trackFormInteraction } from "@/app/utils/web-analytics";

interface MultipleChoicePanelProps {
  panel: Extract<FormConfig["panels"][number], { type: "multiple-choice" }> | GenericMultipleChoicePanel;
  onAnswer: (value: string) => void;
  onBack?: () => void;
  showBack?: boolean;
  isSliding?: boolean;
  isAfterFirstPanel?: boolean;
  initialValue?: string | null;
  locationHeadline?: string;
}

export const MultipleChoicePanel = ({ panel, onAnswer, onBack, showBack, isSliding, isAfterFirstPanel, initialValue = null, locationHeadline }: MultipleChoicePanelProps) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(initialValue);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // Detect mobile screen size
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Set the initial value
    handleResize();
    
    // Add event listener
    window.addEventListener('resize', handleResize);
    
    // Clean up
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOptionClick = (value: string, text: string) => {
    setSelectedOption(value);
    setIsSubmitted(true);
    // Track the interaction
    trackFormInteraction(panel.id, text);
    // Show checkmark, then progress after animation (400ms)
    setTimeout(() => {
      onAnswer(value);
    }, 400);
  };

  // Helper function to render question with line breaks
  const renderQuestion = (question: string) => {
    const lines = question.split('\n').filter(line => line.trim() !== '');
    if (lines.length === 1) {
      return question;
    }
    return (
      <span className="block max-w-[425px]">
        {lines.map((line, index) => (
          <span key={index} className={index > 0 ? 'block pt-5' : 'block'}>
            {line}
          </span>
        ))}
      </span>
    );
  };

  return (
    <div className="w-full flex flex-col items-center">
      {isAfterFirstPanel ? (
        <h2 className="panel-secondary">
          {renderQuestion(panel.question)}
        </h2>
      ) : (
        <>
          <div className="text-center mb-2 font-semibold text-lg md:text-xl" style={{ color: 'rgba(74, 75, 182, 1)' }}>
            {locationHeadline || "America's #1 Tutoring Platform"}
          </div>
          <h1 className="panel-title">
            <span>
              {renderQuestion(panel.question)}
            </span>
          </h1>
        </>
      )}
      <div className="survey-buttons-container">
          {panel.options.map((option) => (
            <div key={option.value} className="relative w-full">
              <button 
                className="button-wrapper w-full" 
                tabIndex={0} 
                aria-label={option.text}
                onClick={() => handleOptionClick(option.value, option.text)}
              >
                <div className="button font-poppins pointer-events-auto relative">
                  <div className="shimmer"></div>
                  <div className="shimmer-reverse"></div>
                  <span className="button-text">{option.text}</span>
                  {selectedOption === option.value && isSubmitted && (
                    <div className="absolute top-1/2 -translate-y-1/2 right-[8px] md:right-[-30px] flex items-center justify-center pointer-events-none z-10">
                      <svg width="24" height="24" viewBox="0 0 30 31" fill="none" xmlns="http://www.w3.org/2000/svg" className="animate-fade-in">
                        <circle cx="15" cy="15.5" r="14.0191" fill="#35AA59" stroke="#35AA59" strokeWidth="1.64937"/>
                        <path d="M8.5 15.5L12.5 19.5L21.5 10.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
              </button>
            </div>
          ))}
      </div>
    </div>
  );
}; 
